/**
 * Substitui o segmento de idioma em uma URL
 * 
 * Exemplo:
 * - https://docs.prodgy.ai/en/guide/welcome -> https://docs.prodgy.ai/pt/guide/welcome
 * - https://prodgy.ai/en/terms -> https://prodgy.ai/pt/terms
 * - https://prodgy.ai/terms -> https://prodgy.ai/pt/terms (adiciona idioma)
 * 
 * @param url - URL base configurada no .env
 * @param currentLanguage - Idioma atual do usuário ('pt' ou 'en')
 * @returns URL com o idioma correto injetado
 */
export const getLocalizedUrl = (url: string | undefined, currentLanguage: string): string => {
  // Se não houver URL ou for fallback, retorna como está
  if (!url || url === '#') return '#';
  
  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/').filter(segment => segment !== '');
    
    // Idiomas suportados
    const supportedLanguages = ['pt', 'en', 'pt-BR', 'en-US'];
    
    // Normalizar idioma para formato curto (pt ou en)
    const normalizedLang = currentLanguage.split('-')[0].toLowerCase();
    
    // Verificar se o primeiro segmento é um idioma
    if (pathSegments.length > 0 && supportedLanguages.some(lang => 
      pathSegments[0].toLowerCase() === lang.toLowerCase() || 
      pathSegments[0].toLowerCase() === lang.split('-')[0].toLowerCase()
    )) {
      // Substituir o idioma existente pelo atual
      pathSegments[0] = normalizedLang;
    } else {
      // Adicionar o idioma no início se não existir
      pathSegments.unshift(normalizedLang);
    }
    
    // Reconstruir o pathname
    urlObj.pathname = '/' + pathSegments.join('/');
    
    return urlObj.toString();
  } catch (error) {
    // Se houver erro ao parsear a URL, retorna a original
    console.warn('[urlLocalization] Erro ao localizar URL:', error);
    return url;
  }
};

/**
 * Hook/Helper para obter URLs localizadas das variáveis de ambiente
 * 
 * @param envVarName - Nome da variável de ambiente (ex: 'VITE_PRODGY_TERMS_URL')
 * @param currentLanguage - Idioma atual do usuário
 * @returns URL localizada ou '#' como fallback
 */
export const getEnvLocalizedUrl = (envVarName: string, currentLanguage: string): string => {
  const baseUrl = (window.ENV?.[envVarName as keyof typeof window.ENV] || 
                   import.meta.env[envVarName]) as string | undefined;
  
  return getLocalizedUrl(baseUrl, currentLanguage);
};
